import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  SmartphoneNfc,
  ShieldCheck,
  Replace,
  History,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { adminApi } from '@/api/endpoints'
import { formatAbsoluteDateTime } from '@/lib/utils'
import type { SimMismatch } from '@/types'

// ── Error helper ────────────────────────────────────────────────────────────

function errorDetail(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string; new_imsi?: string[]; reason?: string[] } } })
      ?.response?.data?.detail ??
    (err as { response?: { data?: { new_imsi?: string[] } } })?.response?.data?.new_imsi?.[0] ??
    fallback
  )
}

// ── Replace IMSI dialog ───────────────────────────────────────────────────────

interface ReplaceImsiDialogProps {
  target: SimMismatch | null
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

function ReplaceImsiDialog({ target, onOpenChange, onSuccess }: ReplaceImsiDialogProps) {
  const [newImsi, setNewImsi] = useState('')
  const [reason, setReason] = useState('')

  // Reset fields whenever a new camera is targeted.
  const [lastId, setLastId] = useState<number | null>(null)
  if (target && target.camera_id !== lastId) {
    setLastId(target.camera_id)
    setNewImsi(target.imsi_reported ?? '')
    setReason('')
  }

  const mutation = useMutation({
    mutationFn: ({ cameraId, imsi, why }: { cameraId: number; imsi: string; why?: string }) =>
      adminApi.replaceImsi(cameraId, imsi, why),
    onSuccess: () => {
      toast.success('Registered IMSI replaced')
      onSuccess()
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      toast.error(errorDetail(err, 'Failed to replace IMSI'))
    },
  })

  const trimmedImsi = newImsi.trim()
  const isValid = /^\d{14,17}$/.test(trimmedImsi)
  const saving = mutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!target || !isValid) return
    mutation.mutate({ cameraId: target.camera_id, imsi: trimmedImsi, why: reason.trim() || undefined })
  }

  return (
    <Dialog.Root open={!!target} onOpenChange={saving ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98]">
          <Dialog.Title className="sr-only">Replace registered IMSI</Dialog.Title>

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Replace registered IMSI</h2>
              {target && (
                <p className="text-sm text-muted-foreground">
                  {target.name} · <span className="font-mono text-xs">{target.imei}</span>
                </p>
              )}
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                New IMSI <span className="text-destructive">*</span>
              </label>
              <Input
                value={newImsi}
                onChange={(e) => setNewImsi(e.target.value)}
                placeholder="14–17 digit IMSI"
                inputMode="numeric"
                className="font-mono"
                autoFocus
              />
              {trimmedImsi.length > 0 && !isValid && (
                <p className="text-xs text-destructive">IMSI must be 14 to 17 digits.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Reason <span className="text-xs text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this change being made?"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={!isValid || saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Replace IMSI
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ── Audit dialog ──────────────────────────────────────────────────────────────

interface AuditDialogProps {
  target: SimMismatch | null
  onOpenChange: (open: boolean) => void
}

function AuditDialog({ target, onOpenChange }: AuditDialogProps) {
  const open = !!target
  const { data: audit = [], isLoading, isError } = useQuery({
    queryKey: ['admin-sim-audit', target?.camera_id],
    queryFn: () => adminApi.getSimAudit(target!.camera_id),
    enabled: open,
  })

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[80vh] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-border bg-background shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
          <Dialog.Title className="sr-only">SIM audit history</Dialog.Title>

          <div className="flex items-start justify-between gap-4 border-b border-border/60 px-5 py-4">
            <div className="space-y-1">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                SIM audit history
              </h2>
              {target && (
                <p className="text-sm text-muted-foreground">
                  {target.name} · <span className="font-mono text-xs">{target.imei}</span>
                </p>
              )}
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="shimmer h-3.5 w-40 rounded" />
                    <div className="shimmer h-3 w-56 rounded" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <AlertTriangle className="h-8 w-8 text-destructive/60 mb-2" />
                <p className="text-sm font-medium">Could not load audit history</p>
                <p className="text-xs text-muted-foreground mt-1">Please try again.</p>
              </div>
            ) : audit.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <History className="h-8 w-8 text-muted-foreground/30 mb-2" />
                <p className="text-sm font-medium">No audit entries yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Changes to this camera's IMSI will appear here.
                </p>
              </div>
            ) : (
              <ol className="space-y-3">
                {audit.map((row) => (
                  <li
                    key={row.id}
                    className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium">
                        {row.changed_by_username ?? 'system'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatAbsoluteDateTime(row.changed_at)}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-xs">
                      <span className="font-mono text-muted-foreground">{row.old_imsi ?? '—'}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <span className="font-mono font-medium">{row.new_imsi ?? '—'}</span>
                    </div>
                    {row.reason && (
                      <p className="mt-1.5 text-xs text-muted-foreground">{row.reason}</p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function SimMismatchesPage() {
  const qc = useQueryClient()

  const [approveTarget, setApproveTarget] = useState<SimMismatch | null>(null)
  const [replaceTarget, setReplaceTarget] = useState<SimMismatch | null>(null)
  const [auditTarget, setAuditTarget] = useState<SimMismatch | null>(null)

  const { data: mismatches = [], isLoading, isError } = useQuery({
    queryKey: ['admin-sim-mismatches'],
    queryFn: adminApi.listSimMismatches,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-sim-mismatches'] })

  const approveMutation = useMutation({
    mutationFn: (cameraId: number) => adminApi.approveReportedImsi(cameraId),
    onSuccess: () => {
      toast.success('Registered IMSI updated to reported value')
      invalidate()
      setApproveTarget(null)
    },
    onError: (err: unknown) => {
      toast.error(errorDetail(err, 'Failed to approve reported IMSI'))
    },
  })

  return (
    <div className="space-y-6">
      {/* #126 — nav bar shows "SIM Mismatches"; drop the redundant title/subtitle. */}

      {/* Table */}
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-body font-semibold flex items-center gap-2">
            <SmartphoneNfc className="h-4 w-4 text-muted-foreground" />
            Mismatched cameras
            {!isLoading && !isError && mismatches.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground">({mismatches.length})</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-4 py-3 border-t border-border/40 first:border-t-0"
                >
                  <div className="flex-1 space-y-1.5">
                    <div className="shimmer h-3.5 w-32 rounded" />
                    <div className="shimmer h-3 w-48 rounded" />
                  </div>
                  <div className="shimmer h-3.5 w-28 rounded" />
                  <div className="shimmer h-3.5 w-28 rounded" />
                  <div className="shimmer h-7 w-24 rounded" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertTriangle className="h-10 w-10 text-destructive/50 mb-3" />
              <p className="text-sm font-medium">Could not load SIM mismatches</p>
              <p className="text-xs text-muted-foreground mt-1">Please refresh and try again.</p>
            </div>
          ) : mismatches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-500/50 mb-3" />
              <p className="text-sm font-medium">No SIM mismatches</p>
              <p className="text-xs text-muted-foreground mt-1">
                Every camera's reported SIM matches what's registered.
              </p>
            </div>
          ) : (
            // On lg+ the table can exceed the content area on smaller laptops (especially
            // 1280–1366 px wide with the sidebar expanded). Wrap in a horizontal scroller
            // and set a min-width on the inner so overflow becomes a clean horizontal scroll
            // inside the Card instead of being clipped by the parent <main>'s overflow-x-hidden.
            // Below lg the rows stack vertically — no min-width is applied, no scroll needed.
            <div className="lg:overflow-x-auto">
            <div className="lg:min-w-[76rem]">
              {/* Column headers (desktop) */}
              <div className="hidden lg:flex items-center gap-3 px-4 py-2 border-b border-border/60 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <div className="flex-1 min-w-0 lg:min-w-[10rem]">Camera</div>
                <div className="w-40 flex-shrink-0">Owner</div>
                <div className="w-44 flex-shrink-0">Registered IMSI</div>
                <div className="w-44 flex-shrink-0">Reported IMSI</div>
                <div className="w-32 flex-shrink-0">Last mismatch</div>
                <div className="w-14 flex-shrink-0 text-right">Count</div>
                <div className="w-64 flex-shrink-0" />
              </div>

              {mismatches.map((m, idx) => (
                <div
                  key={m.camera_id}
                  className={`flex flex-col gap-3 px-4 py-3 hover:bg-muted/40 transition-colors lg:flex-row lg:items-center ${
                    idx > 0 ? 'border-t border-border/40' : ''
                  }`}
                >
                  {/* Camera */}
                  <div className="flex-1 min-w-0 lg:min-w-[10rem]">
                    <p className="text-sm font-medium truncate">{m.name}</p>
                    <p className="text-xs font-mono text-muted-foreground truncate">{m.imei}</p>
                  </div>

                  {/* Owner */}
                  <div className="lg:w-40 lg:flex-shrink-0 min-w-0">
                    <p className="text-sm truncate">
                      {m.owner_username ? `@${m.owner_username}` : '—'}
                    </p>
                    {m.owner_email && (
                      <p className="text-xs text-muted-foreground truncate">{m.owner_email}</p>
                    )}
                  </div>

                  {/* Registered IMSI */}
                  <div className="lg:w-44 lg:flex-shrink-0 min-w-0">
                    <span className="lg:hidden text-[11px] uppercase tracking-wide text-muted-foreground mr-1.5">
                      Registered
                    </span>
                    <span className="text-xs font-mono break-all">{m.imsi_registered ?? '—'}</span>
                  </div>

                  {/* Reported IMSI (emphasized — differs) */}
                  <div className="lg:w-44 lg:flex-shrink-0 min-w-0">
                    <span className="lg:hidden text-[11px] uppercase tracking-wide text-muted-foreground mr-1.5">
                      Reported
                    </span>
                    <span className="text-xs font-mono font-semibold text-amber-600 dark:text-amber-400 break-all">
                      {m.imsi_reported ?? '—'}
                    </span>
                  </div>

                  {/* Last mismatch */}
                  <div className="lg:w-32 lg:flex-shrink-0 text-xs text-muted-foreground">
                    {formatAbsoluteDateTime(m.sim_last_mismatch_at)}
                  </div>

                  {/* Count */}
                  <div className="lg:w-14 lg:flex-shrink-0 lg:text-right">
                    <span className="inline-flex items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25 px-2 py-0.5 text-xs font-semibold">
                      {m.sim_mismatch_count}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-1 lg:w-64 lg:flex-shrink-0 lg:flex-nowrap">
                    <Button
                      size="sm"
                      className="h-7 px-2.5 gap-1.5 bg-amber-500 hover:bg-amber-600 text-amber-950 font-medium"
                      onClick={() => setApproveTarget(m)}
                      title="Approve reported IMSI"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Approve changes
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                      onClick={() => setReplaceTarget(m)}
                      title="Replace IMSI"
                    >
                      <Replace className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setAuditTarget(m)}
                      title="View audit history"
                    >
                      <History className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve confirmation */}
      <ConfirmDialog
        open={!!approveTarget}
        onOpenChange={(open) => { if (!open) setApproveTarget(null) }}
        title="Approve reported IMSI"
        description="Set registered IMSI to the reported value?"
        confirmLabel="Approve changes"
        variant="default"
        confirmClassName="bg-amber-500 hover:bg-amber-600 text-amber-950 font-medium"
        loading={approveMutation.isPending}
        onConfirm={() => approveTarget && approveMutation.mutate(approveTarget.camera_id)}
      />

      {/* Replace IMSI */}
      <ReplaceImsiDialog
        target={replaceTarget}
        onOpenChange={(open) => { if (!open) setReplaceTarget(null) }}
        onSuccess={invalidate}
      />

      {/* Audit history */}
      <AuditDialog
        target={auditTarget}
        onOpenChange={(open) => { if (!open) setAuditTarget(null) }}
      />
    </div>
  )
}
